import React, { useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormattedMessage } from 'react-intl';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { ClassNameMap } from '@mui/styles';
import { useFormContext } from 'react-hook-form';
import HideSourceIcon from '@mui/icons-material/HideSourceSharp';
import CutIcon from '@mui/icons-material/ContentCut';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { NotesClassification } from 'modules/notes/NotesFilterContextProvider';
import { useMessages } from 'modules/intl';
import { HandleTimeSignalActionCallback, NotesActions } from '../hooks';
import { HandleTimeSignalExcludeActionCallback } from '../hooks/useTimeSignalExcludeAction';
import { useFeatureFlag } from '../../../common/hooks';

interface UseStylesProps {
  classes: ClassNameMap<string> | undefined;
  isEmptyLabelsEnabled: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {},
  actions: ({ isEmptyLabelsEnabled }: UseStylesProps) => ({
    fontSize: theme.typography.body1.fontSize,
    margin: isEmptyLabelsEnabled
      ? theme.spacing(0, 0.25, 0, 0.25)
      : theme.spacing(0, -0.5, 0, 1),
    textTransform: 'none'
  }),
  rotateCut: {
    transform: 'rotate(-90deg)'
  }
}));

const useTooltipStyles = makeStyles((theme) => ({
  popper: {
    zIndex: theme.zIndex.tooltip + 2
  }
}));

type TimeSignalQuickActionsByCategoryProps = {
  note: TimeSignal;
  handleTimeSignalAction: HandleTimeSignalActionCallback;
  loading?: boolean;
  classes?: ClassNameMap;
  filterClassification: NotesClassification;
  color?: 'default' | 'primary' | 'secondary' | 'inherit';
  onComplete?: () => void;
  onSplitAction?: () => void;
  handleTimeSignalExcludeAction?: HandleTimeSignalExcludeActionCallback;
  excludeActionLoading?: boolean;
};

const TimeSignalQuickActionsByCategory: React.FC<TimeSignalQuickActionsByCategoryProps> = ({
  note: { classification: category },
  note,
  handleTimeSignalAction,
  loading = false,
  classes: overrideClasses,
  filterClassification,
  color = 'primary',
  onComplete,
  onSplitAction,
  handleTimeSignalExcludeAction,
  excludeActionLoading
}: TimeSignalQuickActionsByCategoryProps) => {
  const isTIWebGroupViewEnabled: boolean = useFeatureFlag(
    'isTIWebGroupViewEnabled',
    false
  );
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);
  const classes = useStyles({
    classes: overrideClasses,
    isEmptyLabelsEnabled: isEmptyLabelsEnabled
  });
  const tooltipClasses = useTooltipStyles();
  const { setValue, unregister } = useFormContext();

  const actionCallback = useCallback(
    async (classificationTarget: NotesActions) => {
      await handleTimeSignalAction({
        notes: [note],
        updates: {
          classificationTarget: classificationTarget
        }
      });
      const id = `${note.date}_${note.id}`;
      if (filterClassification === 'All') {
        setValue(id, false);
      }

      if (
        filterClassification !== 'All' &&
        filterClassification !== classificationTarget
      ) {
        unregister(id);
      }

      onComplete?.();
    },
    [
      handleTimeSignalAction,
      note,
      filterClassification,
      onComplete,
      setValue,
      unregister
    ]
  );
  const timeSignalActions = useMemo(
    () => [
      {
        testId: 'inbox-button',
        show: category === TimeSignalClassification.Unclassified ? false : true,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('Unclassified');
        },
        displayText: 'timeSignalActions.inbox',
        icon: <MoveToInboxIcon />
      },
      {
        testId: 'private-button',
        show: category === TimeSignalClassification.Private ? false : true,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('Private');
        },
        displayText: 'timeSignalActions.private',
        icon: <HideSourceIcon />
      },
      {
        testId: 'accept-button',
        show: category === TimeSignalClassification.Accepted ? false : true,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('Accepted');
        },
        displayText: 'timeSignalActions.accept',
        icon: <ThumbUpIcon />
      },
      {
        testId: 'trash-button',
        show: category === TimeSignalClassification.Trash ? false : true,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('Trash');
        },
        displayText: 'timeSignalActions.trash',
        icon: <DeleteIcon />
      },
      {
        testId: 'delete-forever-button',
        show: category === TimeSignalClassification.Trash ? true : false,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          await actionCallback('DeleteForever');
        },
        displayText: 'timeSignalActions.deleteForever',
        icon: <DeleteForeverIcon />
      },
      {
        testId: 'split-button',
        show: true,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          if (onSplitAction) {
            onSplitAction();
          }
        },
        displayText: 'timeSignalActions.splitNote',
        icon: <CutIcon className={classes.rotateCut} />
      },
      {
        testId: 'diary-include-button',
        show: isTIWebGroupViewEnabled && note.excluded,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          if (handleTimeSignalExcludeAction) {
            await handleTimeSignalExcludeAction({
              note: note,
              excluded: false
            });
            onComplete?.();
          }
        },
        displayText: 'timeSignalActions.diaryIncludeNote',
        icon: <AddCircleIcon />
      },
      {
        testId: 'diary-exclude-button',
        show: isTIWebGroupViewEnabled && !note.excluded,
        handleClick: async (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          if (handleTimeSignalExcludeAction) {
            await handleTimeSignalExcludeAction({
              note: note,
              excluded: true
            });
            onComplete?.();
          }
        },
        displayText: 'timeSignalActions.diaryExcludeNote',
        icon: <RemoveCircleIcon />
      }
    ],
    [
      category,
      classes.rotateCut,
      isTIWebGroupViewEnabled,
      note,
      actionCallback,
      onSplitAction,
      handleTimeSignalExcludeAction,
      onComplete
    ]
  );

  const buttonTexts = useMessages(
    useMemo(() => timeSignalActions.map((t) => t.displayText), [
      timeSignalActions
    ])
  );

  return (
    <Box data-testid="time-signal-quick-actions" className={classes.root}>
      {timeSignalActions.map((action) => {
        return (
          action.show && (
            <IconButton
              key={action.testId}
              className={classes.actions}
              data-testid={action.testId}
              size={'small'}
              color={color}
              aria-label={buttonTexts[action.displayText]}
              onClick={action.handleClick}
              disabled={loading || excludeActionLoading}
            >
              <Tooltip
                title={<FormattedMessage id={action.displayText} />}
                classes={tooltipClasses}
              >
                {action.icon}
              </Tooltip>
            </IconButton>
          )
        );
      })}
    </Box>
  );
};

export default React.memo(TimeSignalQuickActionsByCategory);
